import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useAppStore } from "@utils/app-store";
import {
  OAuthKind,
  Service,
  SSOSettingsParams,
  TermsMeParams,
  TermsParams,
} from "@utils/types";

import {
  getConnections,
  getMe,
  postSignin,
  postSignout,
  getSessions,
  deleteSession,
  postChangePassword,
  postForgotPassword,
  postResetPassword,
  postChangeEmail,
  postRequestEmailChange,
  getSSOSettings,
  postSigninRedirect,
  putProcessEmailChange,
  postOauthGithubSignin,
  postOauthGithubSignup,
  postOauthTwitterSignin,
  postOauthTwitterSignup,
  postOauthGithubSigninRedirect,
  postOauthTwitterSigninRedirect,
  postOauthTwitterConnect,
  postOauthGithubConnect,
  getOauthProfiles,
  getSsoConnections,
  deleteOauthProfile,
  deleteJobConnection,
  putMe,
  postSignup,
  getSignupEmailToken,
  postSignupLink,
  getSSOSigninRedirect,
  getSSOSignup,
  postSamlCallback,
  getSSOConnect,
  getTerms,
  getTermsMe,
  getTermsContent,
  postTermsAccept,
} from "./endpoints";

export const useMe = (enabled = true) => {
  return useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled,
    refetchOnWindowFocus: (query) => {
      const error = query.state.error;

      return !(error instanceof AxiosError && error.response?.status === 451);
    },
    meta: {
      // Special handling for 401 errors:
      // - If the user was previously signed in, redirect to sign-in page
      // - If the user was not signed in, do not redirect (expected 401)
      disableGlobalErrorHandler: (error: Error) => {
        const isAuthenticated = useAppStore.getState().isAuthenticated;

        if (
          isAuthenticated &&
          error instanceof AxiosError &&
          error.response?.status === 401
        ) {
          return false;
        }

        return true;
      },
    },
    staleTime: 0,
  });
};

export const useUpdateMe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useSignup = () => {
  return useMutation({
    mutationFn: postSignup,
  });
};

export const useSignupEmailToken = () => {
  return useMutation({
    mutationFn: getSignupEmailToken,
  });
};

export const useSignupLink = () => {
  return useMutation({
    mutationFn: postSignupLink,
  });
};

export const useSignin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postSignin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    meta: {
      disableGlobalErrorHandler: true,
    },
  });
};

export const useSigninRedirect = () => {
  return useMutation({
    mutationFn: postSigninRedirect,
    meta: {
      disableGlobalErrorHandler: true,
    },
  });
};

export const useOauthTwitterSignup = () => {
  return useMutation({
    mutationFn: postOauthTwitterSignup,
  });
};

export const useOauthTwitterSignin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postOauthTwitterSignin,
    meta: {
      disableGlobalErrorHandler: true,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useOauthTwitterSigninRedirect = () => {
  return useMutation({
    mutationFn: postOauthTwitterSigninRedirect,
  });
};

export const useOauthTwitterConnect = () => {
  return useMutation({
    mutationFn: postOauthTwitterConnect,
  });
};

export const useOauthGithubSignup = () => {
  return useMutation({
    mutationFn: postOauthGithubSignup,
  });
};

export const useOauthGithubSignin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postOauthGithubSignin,
    meta: {
      disableGlobalErrorHandler: true,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useOauthGithubSigninRedirect = () => {
  return useMutation({
    mutationFn: postOauthGithubSigninRedirect,
  });
};

export const useOauthGithubConnect = () => {
  return useMutation({
    mutationFn: postOauthGithubConnect,
  });
};

export const useSignout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postSignout,
    onSuccess: () => {
      // Clear the query cache to remove any cached data related to the user
      queryClient.clear();
    },
  });
};

export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postChangePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useConnections = () => {
  return useQuery({
    queryKey: ["connections"],
    queryFn: getConnections,
  });
};

export const useSessions = () => {
  return useQuery({
    queryKey: ["sessions"],
    queryFn: getSessions,
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
};

export const useSSOSettings = (params: SSOSettingsParams, enabled: boolean) => {
  return useQuery({
    queryKey: ["ssoSettings", params],
    queryFn: () => getSSOSettings(params),
    enabled,
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: postForgotPassword,
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: postResetPassword,
  });
};

export const useChangeEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postChangeEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useRequestEmailChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postRequestEmailChange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useProcessEmailChange = () => {
  return useMutation({
    mutationFn: putProcessEmailChange,
  });
};

export const useOauthProfiles = () => {
  const { data, ...rest } = useQuery({
    queryKey: ["oauthProfiles"],
    queryFn: getOauthProfiles,
  });

  const githubProfile = data?.profiles?.find(
    (profile) => profile.kind === OAuthKind.GITHUB,
  );

  const twitterProfile = data?.profiles?.find(
    (profile) => profile.kind === OAuthKind.TWITTER,
  );

  return {
    githubProfile,
    twitterProfile,
    ...rest,
  };
};

export const useSsoConnections = () => {
  const { data, ...rest } = useQuery({
    queryKey: ["ssoConnections"],
    queryFn: getSsoConnections,
  });

  return {
    connections: data?.connections,
    ...rest,
  };
};

export const useDeleteOauthProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOauthProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["oauthProfiles"] });
    },
  });
};

export const useDeleteJobConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteJobConnection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
};

export const useSSOSigninRedirect = () => {
  return useMutation({
    mutationFn: getSSOSigninRedirect,
  });
};

export const useSSOSignup = () => {
  return useMutation({
    mutationFn: getSSOSignup,
  });
};

export const useSSOConnect = () => {
  return useMutation({
    mutationFn: getSSOConnect,
  });
};

export const useSamlCallback = () => {
  return useMutation({
    mutationFn: postSamlCallback,
  });
};

export const useTerms = (params: TermsParams, enabled = true) => {
  const { data, ...rest } = useQuery({
    queryKey: ["terms", params],
    queryFn: async () => {
      // For default Track Id terms
      if (
        params.service === undefined ||
        params.service === null ||
        params.service === Service.TrackId
      ) {
        const term = await getTerms(params);

        return term ? [term] : [];
      }

      // For other service terms
      const terms = await Promise.all(
        [
          getTerms(params),
          getTerms({ ...params, service: Service.TrackId }),
        ].map((request) =>
          request.catch((error) => {
            // Ignore 404 errors and return null for them
            if (error instanceof AxiosError && error.response?.status === 404) {
              return null;
            }

            throw error;
          }),
        ),
      );

      return terms.filter((term) => term !== null);
    },
    enabled,
    meta: {
      disableGlobalErrorHandler: true,
    },
  });

  return {
    terms: data,
    ...rest,
  };
};

export const useTermsMe = (params: TermsMeParams) => {
  const { data, ...rest } = useQuery({
    queryKey: ["termsMe", params],
    queryFn: () => getTermsMe(params),
    meta: {
      disableGlobalErrorHandler: true,
    },
  });

  return {
    terms: data?.terms,
    ...rest,
  };
};

export const useTermsContent = (termsId?: string, contentLink?: string) => {
  return useQuery({
    queryKey: ["termsContent", termsId, contentLink],
    queryFn: () => getTermsContent(contentLink!),
    enabled: Boolean(termsId && contentLink),
    meta: {
      disableGlobalErrorHandler: true,
    },
  });
};

export const useTermsAccept = () => {
  return useMutation({
    mutationFn: postTermsAccept,
  });
};
